import React, { useId, forwardRef, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import "./Input.scss";
import { Cross2Icon, MagnifyingGlassIcon } from "sebikostudio-icons";
import IconButton from "./IconButton";
import Paragraph from "./Paragraph";

const Input = forwardRef(
  (
    {
      pattern,
      label = "First name",
      placeholder,
      errormsg = "Invalid format.",
      type = "text",
      value,
      onChange,
      className,
      disabled,
      onClick,
      onFocus,
      onBlur,
      icon: Icon,
      validation = false,
      name,
      fluid,
      // New props (optional, backward compatible)
      required = false,
      id,
      hasError, // Explicit error state control
      description, // Help text
      iconRight: IconRight, // Right-side icon
      clearable = false,
      onClear,
      ...props
    },
    ref
  ) => {
    // State to track focus
    const [isFocused, setIsFocused] = useState(false);

    // Generate unique IDs for accessibility
    const generatedId = useId();
    const inputId = id || name || generatedId;
    const errorId = `${inputId}-error`;
    const descriptionId = `${inputId}-description`;

    const isSearchInput = type === "search";

    // Improved error logic - explicit hasError prop takes precedence
    const showError =
      hasError !== undefined ? hasError : validation && !!errormsg;

    // Support for clearable input
    const showClearButton = clearable && value && !disabled;

    // Check if input has value (including 0)
    const hasValue = value !== undefined && value !== null && value !== "";

    // Floating label conditions: has value OR is focused
    const shouldFloat = hasValue || isFocused;

    const inputClasses = classnames("inputfield", className, {
      inputSearch: isSearchInput,
      fluid: fluid,
      hasIcon: !!Icon,
      hasIconRight: !!IconRight || showClearButton,
      hasError: showError,
      disabled: disabled,
      floatingLabel: shouldFloat,
    });

    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    const handleClear = () => {
      if (onClear) {
        onClear();
      } else if (onChange) {
        // Fallback: call onChange with empty value
        onChange({ target: { value: "", name } });
      }
    };

    return (
      <div data-component="input" className={inputClasses} disabled={disabled}>
        {/* Keep existing search icon behavior for backward compatibility */}
        {isSearchInput && (
          <MagnifyingGlassIcon className="search-icon" aria-hidden="true" />
        )}

        {/* Left icon (existing behavior) */}
        {Icon && <Icon className="IconLeft" aria-hidden="true" />}

        <input
          ref={ref}
          id={inputId}
          data-component="input"
          pattern={pattern}
          type={type}
          placeholder={placeholder}
          value={value ?? ""} // Fix: use nullish coalescing for proper 0 handling
          onChange={onChange}
          onClick={onClick}
          name={name || inputId}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          required={required}
          aria-invalid={showError ? "true" : "false"}
          aria-describedby={classnames({
            [errorId]: showError,
            [descriptionId]: description,
          })}
          {...props}
        />

        {/* Right icon (new feature) */}
        {IconRight && !clearable && (
          <IconRight className="IconRight" aria-hidden="true" />
        )}

        {/* Clear button for clearable inputs */}
        {showClearButton && (
          <IconButton
            size="mini"
            variant="ghost"
            onClick={handleClear}
            aria-label="Clear input"
            tabIndex={-1} // Don't interfere with tab order
          >
            <Cross2Icon />
          </IconButton>
        )}

        {/* Description/help text (new feature) */}
        {description && (
          <Paragraph size="large" id={descriptionId}>
            {description}
          </Paragraph>
        )}

        {/* Error message (existing behavior preserved) */}
        {showError && (
          <p id={errorId} className="error" role="alert" aria-live="polite">
            {errormsg}
          </p>
        )}

        {/* Label (keep at bottom for existing CSS compatibility) */}
        {label && <label htmlFor={inputId}>{label}</label>}
      </div>
    );
  }
);

Input.displayName = "Input";

Input.propTypes = {
  // Existing props
  pattern: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  errormsg: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  icon: PropTypes.elementType,
  validation: PropTypes.bool,
  name: PropTypes.string,
  fluid: PropTypes.bool,

  // New optional props
  required: PropTypes.bool,
  id: PropTypes.string,
  hasError: PropTypes.bool, // Explicit error control
  description: PropTypes.string, // Help text
  iconRight: PropTypes.elementType, // Right-side icon
  clearable: PropTypes.bool, // Show clear button
  onClear: PropTypes.func, // Clear handler
};

export default Input;
